'use client';

import React, { useCallback, useMemo } from 'react';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { usePlans } from '@/features/plans/presentation/context/PlansContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { buildChoosePlanRowsFromPlans } from '@/features/plans/presentation/utils/choose-plan-from-plans';
import { formatPlanPriceBrlFromCents, subscriptionStatusShortLabelPt } from '@/core/domain';
import {
  CHOOSE_PLAN_GUARANTEE_BODY,
  CHOOSE_PLAN_GUARANTEE_TITLE,
} from '@/features/plans/presentation/constants/plan-offers-display';
import {
  PLANOS_APP_BADGE_CURRENT_PLAN,
  PLANOS_APP_CURRENT_PLAN_PREFIX,
  PLANOS_APP_CURRENT_PLAN_UNKNOWN_NAME,
  PLANOS_APP_INFO_FOOTER,
  PLANOS_APP_INFO_FOOTER_CANCEL_SCHEDULED,
  PLANOS_APP_NO_ACTIVE_PLAN,
  PLANOS_APP_SECTION_SUBTITLE,
  PLANOS_APP_USER_LOADING,
  planosAppCancelledHeadline,
} from '@/features/plans/presentation/constants/planos-app-section-copy';
import { ShieldCheck } from 'lucide-react';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';
import { APP_SECTION_LOADING_MESSAGE } from '@/features/app/presentation/constants/app-shell-copy';

export const PlanosSection: React.FC = () => {
  const { state: authState } = useAuth();
  const {
    state: { plans, loading, error },
    events: plansEvents,
  } = usePlans();
  const { state: userState, events: userEvents } = useUser();
  const { state: subscriptionState, events: subscriptionEvents } = useSubscription();

  const loadPlanosSection = useCallback(async () => {
    if (!authState.authReady || !authState.user?.uid) return;
    const uid = authState.user.uid;
    await Promise.all([
      plansEvents.refresh(),
      userEvents.getUserById(uid),
      subscriptionEvents.getSubscriptionByUserId(uid),
    ]);
  }, [
    authState.authReady,
    authState.user?.uid,
    plansEvents.refresh,
    userEvents.getUserById,
    subscriptionEvents.getSubscriptionByUserId,
  ]);

  const planosDataReady = useAwaitInitialFetch(loadPlanosSection, [loadPlanosSection]);

  const rows = useMemo(() => buildChoosePlanRowsFromPlans(plans), [plans]);

  const currentPlanId = subscriptionState.currentSubscription?.plan_id ?? null;
  const subscriptionCancelScheduled = Boolean(
    subscriptionState.currentSubscription?.subscription_cancel_at_period_end
  );

  const currentPlanHeadline = useMemo(() => {
    if (!authState.authReady) {
      return PLANOS_APP_USER_LOADING;
    }
    if (!authState.user?.uid) {
      return PLANOS_APP_NO_ACTIVE_PLAN;
    }
    if (userState.loading && !userState.currentUser) {
      return PLANOS_APP_USER_LOADING;
    }
    const u = userState.currentUser;
    const sub = subscriptionState.currentSubscription;
    if (!u) {
      const err = userState.error?.trim();
      return err && err.length > 0 ? err : PLANOS_APP_NO_ACTIVE_PLAN;
    }
    if (sub?.subscription_cancel_at_period_end) {
      const match = sub.plan_id ? plans.find((p) => p.id === sub.plan_id) : undefined;
      const name = match?.title?.trim() ? match.title : PLANOS_APP_CURRENT_PLAN_UNKNOWN_NAME;
      return planosAppCancelledHeadline(name);
    }
    if (sub?.plan_id != null) {
      const match = plans.find((p) => p.id === sub.plan_id);
      const name = match?.title?.trim() ? match.title : PLANOS_APP_CURRENT_PLAN_UNKNOWN_NAME;
      const statusLabel = subscriptionStatusShortLabelPt(sub.subscription_status);
      const base = `${PLANOS_APP_CURRENT_PLAN_PREFIX} ${name}`;
      return statusLabel ? `${base} (${statusLabel})` : base;
    }
    return PLANOS_APP_NO_ACTIVE_PLAN;
  }, [
    authState.authReady,
    authState.user?.uid,
    userState.loading,
    userState.currentUser,
    userState.error,
    subscriptionState.currentSubscription,
    plans,
  ]);

  return (
    <SectionScreenLayout title="Planos" subtitle={PLANOS_APP_SECTION_SUBTITLE}>
      {!planosDataReady ? (
        <div className="flex items-center gap-2 text-text-muted text-sm py-10 justify-center">
          <Spinner size="sm" className="text-cyan" aria-hidden />
          <span>{APP_SECTION_LOADING_MESSAGE}</span>
        </div>
      ) : (
      <div className="max-w-5xl mx-auto w-full space-y-5">
        <p
          className="rounded-lg border border-cyan/25 bg-cyan/5 px-4 py-3 text-sm text-navy-dark font-medium"
          role="status"
        >
          {currentPlanHeadline}
        </p>

        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 text-center">
            {error}
          </p>
        ) : null}

        {!loading && !error && rows.length === 0 ? (
          <p className="text-center text-sm text-text-muted py-10 px-2">
            Nenhum plano disponível no momento. Tente novamente mais tarde.
          </p>
        ) : null}

        {rows.length > 0 ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-3 md:gap-3 lg:gap-4">
              {rows.map((row) => {
                const isCurrent =
                  !subscriptionCancelScheduled &&
                  currentPlanId != null &&
                  row.planId === currentPlanId;
                return (
                  <div
                    key={row.planId}
                    className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-xl"
                  >
                    {/* Vidro + gradiente em toda a altura da célula do grid (evita “corte” quando um card é mais baixo que a linha). */}
                    <div
                      className="pointer-events-none absolute inset-0 rounded-xl animated-gradient-bg"
                      aria-hidden
                    />
                    <div
                      className={`relative z-[1] flex min-h-0 flex-1 flex-col rounded-xl border p-5 sm:p-6 ${
                        row.highlight
                          ? 'border-cyan/60 bg-white/10 shadow-lg shadow-cyan/10 ring-1 ring-cyan/30'
                          : 'border-white/15 bg-white/5'
                      }`}
                    >
                      {isCurrent ? (
                        <span className="mb-3 inline-flex w-fit rounded-full bg-emerald-500/25 px-2.5 py-1 text-xs font-semibold text-emerald-100">
                          {PLANOS_APP_BADGE_CURRENT_PLAN}
                        </span>
                      ) : row.highlight && row.highlightLabel ? (
                        <span className="mb-3 inline-flex w-fit rounded-full bg-cyan/25 px-2.5 py-1 text-xs font-semibold text-cyan-light">
                          {row.highlightLabel}
                        </span>
                      ) : null}
                      {!isCurrent && !row.highlight && row.highlightLabel ? (
                        <span className="mb-3 inline-flex w-fit rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium text-white/80">
                          {row.highlightLabel}
                        </span>
                      ) : null}

                      <h3 className="text-lg font-semibold text-white">{row.title}</h3>

                      <p className="mt-3 flex flex-wrap items-baseline gap-1">
                        <span className="text-3xl font-bold text-white">{row.priceLine}</span>
                        <span className="text-sm text-white/70">{row.priceSub}</span>
                      </p>
                      <p className="mt-1 text-xs text-white/60">{row.billingNote}</p>

                      {row.equivalentMonthlyCents != null ? (
                        <div className="mt-3 rounded-lg border border-cyan/25 bg-cyan/10 px-3 py-2.5 text-xs leading-snug text-cyan-light space-y-2">
                          <p className="font-medium text-white">
                            Equivale a{' '}
                            <strong>{formatPlanPriceBrlFromCents(row.equivalentMonthlyCents)}</strong>
                            /mês no período
                          </p>
                          {row.savingsTotalCents != null && row.savingsTotalCents > 0 ? (
                            <p className="text-white/90">
                              Economia de{' '}
                              <strong className="text-cyan-light">
                                {formatPlanPriceBrlFromCents(row.savingsTotalCents)}
                              </strong>
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="rounded-xl border border-cyan/20 bg-cyan/5 px-4 py-4 sm:px-5 sm:py-4"
              role="region"
              aria-label="Garantia para todos os planos"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-cyan-dark">
                  <ShieldCheck className="h-5 w-5" aria-hidden />
                </div>
                <div className="space-y-1 text-sm text-navy-dark">
                  <p className="font-semibold">{CHOOSE_PLAN_GUARANTEE_TITLE}</p>
                  <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                    {CHOOSE_PLAN_GUARANTEE_BODY}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-text-muted text-center leading-relaxed px-1">
              {subscriptionCancelScheduled ?
                PLANOS_APP_INFO_FOOTER_CANCEL_SCHEDULED
              : PLANOS_APP_INFO_FOOTER}
            </p>
          </div>
        ) : null}
      </div>
      )}
    </SectionScreenLayout>
  );
};
